import { Barn } from '../../settings/models/barn.model';

export const FLOCK_VARIANT = {
  Layer: 'layer',
  Breeder: 'breeder',
} as const;

export type FlockVariant = (typeof FLOCK_VARIANT)[keyof typeof FLOCK_VARIANT];

export interface InputDefinition {
  mortality: string;
  eggsAmount: string;
  eggsAmountSecondKind: string;
  eggWeight: string;
  forage: string;
  water: string;
  henWeight: string;
  moult: string;
}

export interface BreedCategory {
  id: number;
  name: string;
}

export interface Breed {
  id: number;
  name: string;
}

export interface HenHatchery {
  id: number;
  externalId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface RearingOrganization {
  id: number;
  name: string;
}

export interface HenDeparture {
  departureDate: string;
  amount: number;
  weightInGrams: number;
  eggsPrice?: number;
  eggsPriceSecondKind?: number;
  hensPrice?: number;
}

export interface BaseDayRegistration<TValueRegistration> {
  day: number;
  registrationData: TValueRegistration;
}

export interface BaseWeekRegistration<TValueRegistration> {
  weekNr: number;
  startDay: number;
  endDay: number;
  dayRegistrations: BaseDayRegistration<TValueRegistration>[];
  registrationData: TValueRegistration;
}

export interface BaseFlock<TArrival, TWeekRegistration> {
  id: number;
  name: string;
  customerId: number;
  locationId: number;
  arrival: TArrival;
  inputDefinition: InputDefinition;
  departure: HenDeparture;
  weekRegistrations: TWeekRegistration[];
}

export enum FlockSettingsKeys {
  CONTEXT = 'layer',
  SELECTED_FLOCK_ID = 'selectedFlockId',
  NEW_FLOCK_ADDED = 'newFlockAdded',
  FLOCK_WEEK_CHART_FILTERS = 'flockWeekChartFilters',
  FLOCK_WEEK_REGISTRATION_FILTERS = 'flockWeekRegistrationFilters',
  BREEDER_FLOCK_WEEK_REGISTRATION_FILTERS = 'breederFlockWeekRegistrationFilters',
  FLOCK_WEEK_REGISTRATION_DETAILED = 'flockWeekRegistrationDetailed',
}

export interface FlockWeekChartFilters {
  breedCategoryName: string;
  eggMarketingStandard: number;
  breedName: string;
  showCurrentFlock: boolean;
  showBreedersReferenceBenchmark: boolean;
  showPorphyrioBenchmark: boolean;
  showOtherFlock: boolean;
  selectedOtherFlockId?: number;
}

export const createDefaultFlockWeekChartFilters = (): FlockWeekChartFilters => ({
  breedCategoryName: 'overall',
  eggMarketingStandard: 2,
  breedName: 'Overall',
  showCurrentFlock: true,
  showBreedersReferenceBenchmark: true,
  showPorphyrioBenchmark: true,
  showOtherFlock: false,
});

export interface PorphyrioCategories {
  categories: PorphyrioCategory[];
}

export interface PorphyrioCategory {
  category: string;
  eggMarketingStandardsFound: PorphyrioEggMarketingStandardFound[];
}

export interface PorphyrioEggMarketingStandardFound {
  standard: number;
  breeds: string[];
}

export interface WeekRegistrationFilters<TWeekRegistrationKpiEnum = string> {
  kpiFilters: TWeekRegistrationKpiEnum[];
  manualInput: TWeekRegistrationKpiEnum[];
  isMoultSelected?: boolean;
  isRemarksSelected?: boolean;
}

export enum IntervalType {
  Day = 'Day',
  Week = 'Week',
}

export enum EditMode {
  Add = 'add',
  Edit = 'edit',
}

export interface FarmResource {
  id: number;
  year: number;
  electricity?: number;
  electricityPhotovoltaic?: number;
  electricityWind?: number;
  naturalGas?: number;
  diesel?: number;
  gasoline?: number;
  coal?: number;
  propaneLpg?: number;
  water?: number;
}

export interface FarmResourcesDivision {
  flockId: number;
  electricity?: number;
  electricityPhotovoltaic?: number;
  electricityWind?: number;
  naturalGas?: number;
  diesel?: number;
  gasoline?: number;
  coal?: number;
  propaneLpg?: number;
  water?: number;
}

export interface CarbonFootprintResponse {
  results: CarbonFootprintResultFound[];
}

export interface CarbonFootprintResultFound {
  id: number;
  customerId: number;
  locationId: number;
  flockId: number;
  sustellFarmBaselineId: string;
  carbonFootprintResult: CarbonFootprintResult;
}

export interface CarbonFootprintResult {
  status: ProcessingStatus;
  footprint: Footprint;
}

export enum ProcessingStatus {
  PENDING = 'PENDING',
  FINISHED = 'FINISHED',
}

export interface Footprint {
  animalType: string;
  stagesImpact: StageImpact[];
  errors: Error[];
}

export interface Impact {
  name: string;
  value: number;
  unit: string;
}

export interface Error {
  type: string;
  key: string;
  messages: string[];
}

export interface StageImpact {
  animalType: string;
  impacts: Impact[];
}

export interface ImpactRow {
  name: string;
  [animalType: string]: string | number | undefined;
}

export type SharedBarn = Barn;
